import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
/**
 * Создает менеджер для динамического монтирования React-компонентов
 * Позволяет добавлять и удалять компоненты в runtime без перерисовки родительского компонента
 *
 * @returns Объект с методами для управления монтированием компонентов
 * @returns {Function} mount - Функция для добавления компонента в дерево
 * @returns {Function} unmount - Функция для удаления компонента из дерева по ID
 * @returns {Function} clear - Функция для очистки всех компонентов
 * @returns {Component} MountPoint - React-компонент для рендеринга монтированных компонентов
 */
export function createReactTreeMounter() {
    let idCounter = 0;
    const componentsToMount = {};
    let onMount = null;
    /**
     * Добавляет компонент в дерево для монтирования
     *
     * @param componentToMount - React-компонент для монтирования
     * @returns {number} Уникальный идентификатор компонента для последующего удаления
     */
    function mount(componentToMount) {
        const id = idCounter++;
        componentsToMount[id] = componentToMount;
        onMount?.(componentsToMount);
        return id;
    }
    /**
     * Удаляет компонент из дерева по идентификатору
     *
     * @param id - Идентификатор компонента, полученный при вызове mount()
     */
    function unmount(id) {
        delete componentsToMount[id];
        onMount?.(componentsToMount);
    }
    /**
     * Очищает все монтированные компоненты из дерева
     */
    function clear() {
        for (const id in componentsToMount) {
            delete componentsToMount[id];
        }
        onMount?.(componentsToMount);
    }
    /**
     * Устанавливает callback-функцию для обновления состояния монтирования
     *
     * @param callback - Функция, вызываемая при изменении списка компонентов
     * @returns {Function} Функция для отмены подписки на изменения
     */
    function setOnMount(callback) {
        onMount = callback;
        onMount?.(componentsToMount);
        return () => {
            // Сбрасываем onMount только тогда, когда он не был перезаписан другим компонентом MountPoint
            if (onMount === callback) {
                onMount = null;
            }
        };
    }
    return {
        mount,
        unmount,
        clear,
        MountPoint: createMountPoint(setOnMount),
    };
}
/**
 * Создает React-компонент для рендеринга монтированных компонентов
 *
 * @param setOnMount - Функция для установки callback'а обновления
 * @returns {Function} React-компонент MountPoint
 */
function createMountPoint(setOnMount) {
    /**
     * Компонент для рендеринга всех монтированных компонентов
     * Использует React.Fragment для корректного отображения списка компонентов
     *
     * @returns {JSX.Element} React-элемент с монтированными компонентами
     */
    return function MountPoint() {
        const [componentsToMount, setComponentsToMount] = useState({});
        useEffect(() => {
            return setOnMount(components => {
                setComponentsToMount({ ...components });
            });
        }, []);
        return (_jsx(_Fragment, { children: Object.entries(componentsToMount).map(([id, component]) => (_jsx(React.Fragment, { children: component }, id))) }));
    };
}
